'use client';

import { useTranslations } from 'next-intl';
import { Check, PlayCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatCellValue } from '@/lib/build-dynamic-form-columns';
import { useRouter } from '@/i18n/routing';
import type { DynamicFormField } from '@/features/dynamic-forms';
import type { Registration } from '@/features/registration';

interface ReviewCardProps {
  registration: Registration;
  dynamicFormFields?: DynamicFormField[];
}

const STAGE_STYLES: Record<string, string> = {
  review:
    'bg-purple-100 text-purple-800 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-200',
  ready_for_packing:
    'bg-emerald-100 text-emerald-800 hover:bg-emerald-100 dark:bg-emerald-900 dark:text-emerald-200',
  completed:
    'bg-green-100 text-green-800 hover:bg-green-100 dark:bg-green-900 dark:text-green-200'
};

const REVIEW_STAGE_STATUS_STYLES: Record<string, string> = {
  ready_for_review: 'bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200',
  returned_for_review:
    'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  reviewed:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
};

export function ReviewCard({
  registration,
  dynamicFormFields
}: ReviewCardProps) {
  const t = useTranslations('review');
  const tc = useTranslations('common');
  const router = useRouter();

  const stage = registration.current_stage;
  const isCompleted = registration.review_completed_at != null;

  const stageLabels: Record<string, string> = {
    sticking: tc('stageSticking'),
    review: tc('stageReview'),
    ready_for_packing: tc('stageReadyForPacking'),
    completed: tc('stageCompleted')
  };

  const stageStatusLabels: Record<string, string> = {
    ready_for_review: t('subStatusReadyForReview'),
    returned_for_review: t('subStatusReturnedForReview'),
    reviewed: t('subStatusReviewed')
  };
  const subStatus = registration.stage_status ?? null;

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        {/* Top: container number + stage badge */}
        <div className='mb-4 flex items-start justify-between'>
          <div>
            <button
              type='button'
              className='text-primary font-semibold hover:underline'
              onClick={() =>
                router.push(
                  `/dashboard/containers/${registration.id}?stage=review`
                )
              }
            >
              {registration.container_number}
            </button>
          </div>
          <div className='flex flex-wrap items-center gap-1'>
            <Badge
              variant='secondary'
              className={cn('shrink-0 text-xs', STAGE_STYLES[stage] ?? '')}
            >
              {stageLabels[stage] ?? stage}
            </Badge>
            {subStatus && stageStatusLabels[subStatus] && (
              <Badge
                variant='secondary'
                className={cn(
                  'shrink-0 text-xs',
                  REVIEW_STAGE_STATUS_STYLES[subStatus] ?? ''
                )}
              >
                {stageStatusLabels[subStatus]}
              </Badge>
            )}
          </div>
        </div>

        {/* Entity name */}
        {registration.entity_name_ar && (
          <p className='text-foreground mb-3 truncate text-sm font-medium'>
            {registration.entity_name_ar}
          </p>
        )}

        {/* Dynamic form fields */}
        {dynamicFormFields && dynamicFormFields.length > 0 ? (
          <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
            {dynamicFormFields.slice(0, 5).map((field) => {
              const responses = registration.form_responses as Record<
                string,
                unknown
              > | null;
              return (
                <div key={field.id} className='flex items-start gap-1.5'>
                  <span className='shrink-0 font-medium'>{field.label}:</span>
                  <span className='truncate'>
                    {formatCellValue(responses?.[String(field.id)], field.type)}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          registration.latest_batch_id && (
            <div className='text-muted-foreground mb-4 space-y-1 text-sm'>
              <div className='flex items-center gap-1.5'>
                <span>
                  {t('batchPages', {
                    start: registration.latest_batch_start_page ?? '—',
                    end: registration.latest_batch_end_page ?? '—'
                  })}
                </span>
                <Badge
                  variant='secondary'
                  className='bg-green-100 px-1.5 py-0 text-[10px] text-green-800 dark:bg-green-900 dark:text-green-200'
                >
                  <Check className='me-0.5 h-2.5 w-2.5' />
                  {t('saved')}
                </Badge>
              </div>
            </div>
          )
        )}

        {/* Bottom: action button */}
        <div className='flex items-center justify-end border-t pt-3'>
          {isCompleted ? (
            <Button
              variant='outline'
              size='sm'
              onClick={() =>
                router.push(`/dashboard/review/${registration.id}`)
              }
            >
              {t('viewContainer')}
            </Button>
          ) : (
            <Button
              variant='default'
              size='sm'
              className='bg-green-600 text-white hover:bg-green-700'
              onClick={() =>
                router.push(`/dashboard/review/${registration.id}`)
              }
            >
              <PlayCircle className='me-1.5 h-4 w-4' />
              {t('startReview')}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
